import { ChangeEvent, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button, DialogActions } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useSnackbar } from 'notistack';
import { TFormType, TGroupFormData } from 'src/@types';
import { RegUpdText } from 'src/components/custom-regUpd-text/RegUpdText';
import FormProvider, { RHFTextField } from 'src/components/hook-form';
import { ALERT_MSG } from 'src/components/settings/constEnum';
import {
  FormInnerContainer,
  FormItem,
  FormLabel,
  FormRow,
  StyledLoadingButton,
} from 'src/layouts/form/FormLayout';
import { IGroup, useStores } from 'src/models';
import { groupApi } from 'src/service';
import { useHandleResponse } from 'src/service/useHandleResponse';
import { FORM_IDS, FormType } from 'src/static/constants/common/form';
import { getGroupFormDefaultValues } from 'src/utils/getRHFDefaultValues';

interface GroupFormDialogProps {
  formType: TFormType;
  onSubmitSuccess: () => void;
  selectedGroup?: IGroup;
}
const GroupFormDialog = observer(
  ({ formType, onSubmitSuccess, selectedGroup }: GroupFormDialogProps) => {
    const { t } = useTranslation();
    const { alertStore } = useStores();
    const { enqueueSnackbar } = useSnackbar();
    const handleResponse = useHandleResponse();
    const isCreate = formType === FormType.CREATE;
    const methods = useForm<TGroupFormData>({
      mode: 'all',
      defaultValues: getGroupFormDefaultValues(selectedGroup),
    });
    const { setValue, handleSubmit, getValues, formState } = methods;
    const { isValid } = formState; // 폼 유효성 체크

    // 중복확인 상태
    const [isConfirmDuplicate, setIsConfirmDuplicate] = useState(false);
    // 중복확인 버튼 로딩 상태
    const [checkDuplicateButtonLoading, setCheckDuplicateButtonLoading] = useState(false);
    // 그룹명 중복확인
    const handleCheckGroupNmDuplicate = async () => {
      setCheckDuplicateButtonLoading(true);
      try {
        const groupNm = getValues('oprtrGroupNm');
        if (groupNm.trim()) {
          const result = await groupApi.checkDuplicateGroupNm(groupNm);
          if (result?.data) {
            // 중복되지 않은 그룹명
            enqueueSnackbar('사용 가능한 이름입니다.', { variant: 'success' });
            setIsConfirmDuplicate(true);
            return;
          }
          // 중복된 아이디
          enqueueSnackbar('이미 사용중인 이름입니다.', { variant: 'error' });
        }
      } catch (error) {
        console.error(error);
      } finally {
        setCheckDuplicateButtonLoading(false);
      }
    };

    // 아이디 변경 시 중복확인 상태 초기화
    const handleGroupNmChange = (e: ChangeEvent<HTMLInputElement>) => {
      setIsConfirmDuplicate(false);
      setValue('oprtrGroupNm', e.target.value);
    };

    // 등록 / 수정 함수
    const onSubmit = async (data: TGroupFormData) => {
      // 등록 시 중복확인 상태가 아니라면 중복확인을 요청하라는 알림
      if (isCreate && !isConfirmDuplicate) {
        enqueueSnackbar(ALERT_MSG.systemManagement.usrGrpMgmt.duplicateCheckLoginId, {
          variant: 'error',
        });
        return;
      }
      const groupInfo = {
        oprtrGroupNm: data.oprtrGroupNm,
        roleIdList: data.roleIdList,
        useYn: data.useYn,
        dscrpt: data.dscrpt,
      };

      const responseText = isCreate ? '등록' : '수정';
      const result =
        !isCreate && selectedGroup?.oprtrGroupSid
          ? groupApi.updateGroup(selectedGroup?.oprtrGroupSid, groupInfo)
          : groupApi.createGroup(groupInfo);
      if (await result) {
        handleResponse(
          `${responseText}이 완료되었습니다.`,
          `${responseText}이 실패되었습니다.`,
          onSubmitSuccess,
        );
      } else {
        console.error(`${responseText} 실패`);
      }
    };

    // 삭제
    const handleDelete = async () => {
      alertStore.setOnConfirm(async () => {
        if (!selectedGroup?.oprtrGroupSid) return;
        groupApi
          .removeGroup(selectedGroup?.oprtrGroupSid)
          .then(() =>
            handleResponse('삭제가 완료되었습니다.', '삭제가 실패되었습니다.', onSubmitSuccess),
          );
      });
      alertStore.setProps({
        open: true,
        title: '그룹 삭제',
        content: '해당 그룹을 삭제하시겠습니까?',
        category: 'error',
        hasCancelButton: true,
      });
    };
    return (
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)} formId={FORM_IDS.GROUP}>
        <FormInnerContainer>
          <FormLabel>{t('group.form.sectionTitle')}</FormLabel>
          <FormRow sx={{ gridTemplateColumns: formType === FormType.CREATE ? '4fr 0.5fr' : '1fr' }}>
            <FormItem>
              <RHFTextField
                label={t('group.form.oprtrGroupNm')}
                name="oprtrGroupNm"
                readonly={!isCreate}
                placeholder={t('group.form.oprtrGroupNmPlaceholder')}
                onChange={handleGroupNmChange}
              />
            </FormItem>
            {formType === FormType.CREATE && (
              <FormItem>
                <StyledLoadingButton
                  variant="outlined"
                  color="primary"
                  onClick={handleCheckGroupNmDuplicate}
                  loading={checkDuplicateButtonLoading}
                  disabled={isConfirmDuplicate}
                >
                  {t('common.button.checkDuplicate')}
                </StyledLoadingButton>
              </FormItem>
            )}
          </FormRow>
          <FormRow sx={{ gridTemplateColumns: '1fr' }}>
            <FormItem>
              <RHFTextField label={t('group.form.dscrpt')} name="dscrpt" />
            </FormItem>
          </FormRow>
        </FormInnerContainer>
        {!isCreate && (
          <RegUpdText
            updDt={Date.parse(selectedGroup?.updDt?.toString() ?? '')}
            userNm={selectedGroup?.updUser?.loginId ?? '-'}
          />
        )}

        <DialogActions>
          {!isCreate && (
            <Button onClick={handleDelete} variant="contained" color="error">
              {t('common.button.delete')}
            </Button>
          )}
          <Button type="submit" variant="contained" form={FORM_IDS.GROUP} disabled={!isValid}>
            {isCreate ? t('common.button.register') : t('common.button.edit')}
          </Button>
        </DialogActions>
      </FormProvider>
    );
  },
);

export default GroupFormDialog;
